import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
/* eslint-disable camelcase */
import type { UploadVideo, Video } from '@/services/videos/types'
import { OrientationFilterOption } from '@/components/content/library/filters/utils'
import { ButtonVariant } from '@/styles/types'
import { ImageTypes } from '@/constants'
import { ModalType } from '@/components/modals/modals-util'
import { Component, Ref } from 'vue'
import { InfoBarTypes } from '@/components/common/types'
export interface AxiosErrorResponse {
  message: string
  status?: number
  details?: string
  error: {
    description: string
    code?: string
  }
}

export interface CustomAxiosError extends AxiosError {
  config: AxiosRequestConfig
  request?: any
  response: AxiosResponse<AxiosErrorResponse>
}

export type WelcomeCard = {
  imgSrc: string
  title: string
  subtitle: string
  path: string
}

export interface LibraryFilter {
  packages: number[]
  duration: number[]
  startDate: string
  endDate: string
  categories: number[]
  creatorIds: number[]
  pricing: string
  uploadDate: string
  sortBy: string | SortByOptions
  sortOrder: string | SortOrder
  languages: number[]
  search: string
  page_number: number
  is_private: boolean
  ownership: boolean
  show_packages: boolean
  show_folders: boolean
  show_public: boolean
  results_per_page: number
  folderId: number
  orientation: OrientationFilterOption
  tags: number[]
  source_type: string
}

export interface FilterTag {
  key: string
  id: number
  filterType: keyof LibraryFilter
  icon: string
  label: string
}

export interface BaseEntity {
  id: number
  name?: string
}

export type Category = BaseEntity
export type Tag = BaseEntity
export type Interest = BaseEntity

export interface VideoTag {
  name: string
  id?: number
  __CREATE__?: boolean
}

export interface VideoCategory {
  category: Category
  videos: Video[]
}

export interface Language {
  id: number
  language: string
  '2letter': string
  '3letter': string
}

export interface Creators {
  creators: {
    map(arg0: (l: any) => boolean): unknown
    organization_id: number
    name: string
  }
}

export enum VideoAccessFilters {
  All = '',
  Public = 'public',
  Private = 'private'
}

export interface MyVideoFilters {
  show_public?: boolean
  pricing_model?: string
  is_vertical?: boolean
  duration_min?: number
  duration_max?: number
  languages?: number[]
}

export enum FilterType {
  VideoAccess = 'videoAccess',
  Language = 'language',
  PricingModel = 'pricing',
  Duration = 'duration',
  ContentOrientation = 'orientation',
  Folder = 'folder',
  Status = 'status',
  Type = 'type',
  Category = 'category',
  TimeRange = 'timeRange',
  Device = 'device',
  VideoDraftStatus = 'videoDraftStatus'
}

export enum TimeRangeFilters {
  All = 'all',
  LastHour = 'last_h',
  Last24Hours = 'last_24h',
  Last7Days = 'last_7d',
  Last30Days = 'last_30d',
  LastYear = 'last_1y'
}

export interface FilterConfig<T extends FilterType, V> {
  type: T
  title: string
  defaultValue: V
  component: any
  transform?: (value: V) => Record<string, any>
  componentProps?: Record<string, any>
}

export interface FilterState<T> {
  value: T
  tempValue: T
  isOpen: boolean
  isApplied: boolean
}

export enum StatusFilter {
  All = 'all',
  Pending = 'pending',
  Active = 'active',
  Inactive = 'inactive',
  Rejected = 'rejected'
}

export enum TypeFilterValue {
  All = 'all',
  Youtube = 'youtube_channel',
  Youtube_playlist = 'youtube_playlist',
  Mrss = 'mrss',
  Tiktok = 'tiktok',
  Instagram = 'instagram'
}

export enum PricingModelFilters {
  All = '',
  Revshare = 'revshare',
  Free = 'free'
}

export enum ContentOrientationFilters {
  All = '',
  Horizontal = 'horizontal',
  Vertical = 'vertical'
}

export type LanguageFilterValue = { id: number; label: string; selected: boolean }[]
export type CategoryFilterValue = { id: number; label: string; selected: boolean }[]
export type DurationFilterValue = [number, number | typeof Infinity]
export type FolderFilterValue = (number | string)[]

export interface VideoFilterValueMap {
  [FilterType.VideoAccess]: VideoAccessFilters
  [FilterType.Language]: LanguageFilterValue
  [FilterType.PricingModel]: PricingModelFilters
  [FilterType.Duration]: DurationFilterValue
  [FilterType.ContentOrientation]: ContentOrientationFilters
}

export interface DraftFilterValueMap {
  [FilterType.VideoDraftStatus]: VideoDraftStatus
}

export interface ImportFilterValueMap {
  [FilterType.Folder]: FolderFilterValue
  [FilterType.VideoAccess]: VideoAccessFilters
  [FilterType.Status]: StatusFilter
  [FilterType.Type]: TypeFilterValue
  [FilterType.Category]: CategoryFilterValue
}

export enum AiGeneratedPairedFilter {
  All = 'all',
  Paired = 'paired',
  Unpaired = 'unpaired'
}

export interface AiGeneratedFilterValueMap {
  [FilterType.ContentOrientation]: ContentOrientationFilters
  [FilterType.Status]: AiGeneratedPairedFilter
}

export type AiGeneratedFiltersState = {
  [K in keyof AiGeneratedFilterValueMap]: FilterState<AiGeneratedFilterValueMap[K]>
}

export type AiGeneratedPairedFilterConfig = FilterConfig<FilterType.Status, AiGeneratedPairedFilter>

export type AiGeneratedFilterConfigArray = [ContentOrientationFilterConfig, AiGeneratedPairedFilterConfig]

export type FilterValue =
  | VideoAccessFilters
  | LanguageFilterValue
  | PricingModelFilters
  | DurationFilterValue
  | ContentOrientationFilters
  | FolderFilterValue
  | StatusFilter
  | AiGeneratedPairedFilter
  | TypeFilterValue
  | CategoryFilterValue
  | DeviceFilter
  | WidgetTypeFilter
  | VideoDraftStatus

export interface Creator {
  organization_id: number
  name: string
}

export interface BaseFilterConfig<T extends FilterValue> {
  type: FilterType
  title: string
  defaultValue: T
  component: any
  transform?: (value: T) => Record<string, any>
  componentProps?: Record<string, any>
}

export interface FilterState<T extends FilterValue> {
  value: T
  tempValue: T
  isOpen: boolean
  isApplied: boolean
}

export type VideoFilterConfig = BaseFilterConfig<VideoFilterValueMap[keyof VideoFilterValueMap]>
export type ImportFilterConfig = BaseFilterConfig<ImportFilterValueMap[keyof ImportFilterValueMap]>

export type VideoFiltersState = {
  [K in keyof VideoFilterValueMap]: FilterState<VideoFilterValueMap[K]>
}

export type DraftFiltersState = {
  [K in keyof DraftFilterValueMap]: FilterState<DraftFilterValueMap[K]>
}

export type ImportFiltersState = {
  [K in keyof ImportFilterValueMap]: FilterState<ImportFilterValueMap[K]>
}

export type VideoAccessFilterConfig = FilterConfig<FilterType.VideoAccess, VideoAccessFilters>
export type LanguageFilterConfig = FilterConfig<FilterType.Language, LanguageFilterValue>
export type PricingModelFilterConfig = FilterConfig<FilterType.PricingModel, PricingModelFilters>
export type DurationFilterConfig = FilterConfig<FilterType.Duration, DurationFilterValue>
export type ContentOrientationFilterConfig = FilterConfig<FilterType.ContentOrientation, ContentOrientationFilters>

export type VideoFilterConfigArray = [VideoAccessFilterConfig, LanguageFilterConfig, PricingModelFilterConfig, DurationFilterConfig, ContentOrientationFilterConfig]

export type VideoDraftStatusFilterConfig = FilterConfig<FilterType.VideoDraftStatus, VideoDraftStatus>
export type DraftFilterConfigArray = [VideoDraftStatusFilterConfig]

export type FolderFilterConfig = FilterConfig<FilterType.Folder, FolderFilterValue>
export type StatusFilterConfig = FilterConfig<FilterType.Status, StatusFilter>
export type TypeFilterConfig = FilterConfig<FilterType.Type, TypeFilterValue>
export type CategoryFilterConfig = FilterConfig<FilterType.Category, CategoryFilterValue>

export type ImportFilterConfigArray = [TypeFilterConfig, VideoAccessFilterConfig, StatusFilterConfig, CategoryFilterConfig, FolderFilterConfig]

export interface ReportColumn {
  column_name: string
  orderable: boolean
  title: string
  is_default: boolean
  is_selected: boolean
  searchable: boolean
  is_dimension: boolean
}

export interface DataDisplayController {
  setFilters(filters?: Record<string, string | boolean | string[] | boolean[] | number[] | number | undefined>): void
  resetToDefaultPageAndGetData: () => void
  setSearchValue: (value: string) => void
  refreshData: () => void
  isLoading: boolean
  bulkSelect?: {
    reset: () => void
  }
}

export interface ReportColumns {
  columns: ReportColumn[]
}

export enum EmbedTypes {
  None,
  Code,
  Url
}

export enum VideoStatus {
  Init = 'init',
  Request = 'request',
  Uploaded = 'uploaded',
  Register = 'register',
  Uploading = 'uploading',
  Fail = 'fail'
}

export enum VideoDraftStatus {
  All = 'all',
  NoSettings = 'no_settings',
  SettingsSet = 'settings_set'
}

export enum VideoEditVideoSettingsScreen {
  EditVideo = 'editvideo',
  PairedUrls = 'pairedurls'
}

export enum VideoDetailScreenTypes {
  AddToPlaylist = 'addtoplaylist',
  PairedUrls = 'pairedurls'
}

export enum PlaylistEditScreenTypes {
  EditPlaylist = 'editplaylist',
  Embeded = 'embeded'
}

export interface TargetingUrl {
  id: number
  organization_id: number
  url: string
  video_id: string
  create_date: string
}

export interface VideoSettingsSetupForm {
  language_id: number | null
  private: boolean | null
  pricing_model: string
  folder: number | null
  categories: number[] | null
  hasCountryRestrictions: boolean | null
  countryRestrictionType: string | null
  countries_allow: string[] | null
  countries_disallow: string[] | null
  guid: string
  clickthroughUrls: { url: string }[]
  tags: VideoTag[] | null
}

export interface SettingsConflicts {
  hasConflicts: boolean
  language_id: boolean
  private: boolean
  pricing_model: boolean
  folder: boolean
  categories: boolean
  hasCountryRestrictions: boolean
  countryRestrictionType: boolean
  countries_allow: boolean
  countries_disallow: boolean
  guid: boolean
  clickthroughUrls: boolean
  tags: boolean
}

export type VideoSettingsDefaultsType = {
  private: boolean
  pricing_model: 'rev_share'
  language_id: number
  folder: number | null
  categories: number[]
  guid: string
  clickthroughUrls: string
  tags: Array<{ name: string; id?: number }> | null
  hasCountryRestrictions: boolean
  countryRestrictionType: string | null
  countries_allow: string[]
  countries_disallow: string[]
}

export enum VideoSettingsField {
  LanguageId = 'language_id',
  Private = 'private',
  PricingModel = 'pricing_model',
  Folder = 'folder',
  Categories = 'categories',
  HasCountryRestrictions = 'hasCountryRestrictions',
  CountryRestrictionType = 'countryRestrictionType',
  CountriesAllow = 'countries_allow',
  CountriesDisallow = 'countries_disallow',
  Guid = 'guid',
  ClickthroughUrls = 'clickthroughUrls',
  Tags = 'tags'
}

export interface AgencyMostActiveAccounts {
  title: string
  sorted: boolean
  sortable: boolean
  orderBy: string
  sortBy: string
}

export const DashboardGraphParameters = {
  agencyActivityServerCalls: ['server_calls_timeline', 'revenue_timeline'],
  agencyActivityPlayersLoads: ['player_loads_timeline', 'revenue_timeline'],
  agencyQuality: ['ad_viewability_timeline', 'fill_rate_timeline'],
  contentActivity: ['content_views_timeline', 'revenue_timeline'],
  publisherActivityServerCalls: ['revenue_timeline', 'server_calls_timeline'],
  publisherActivityPlayerLoads: ['revenue_timeline', 'player_loads_timeline'],
  publisherQuality: ['viewability_imps_timeline', 'viewability_fill_timeline']
}

export enum DashboardPublisherActivityMetrics {
  revenue = 'revenue', // (for publisher_activity_top)
  cpm = 'vcpm', // (for publisher_activity_top)
  fill = 'fill', // (for publisher_activity_top))
  server_calls = 'server_calls' // (for publisher_activity_top)
  // content_views = 'content_views', // (for publisher_quality_top)
}

export enum DashboardAgencyActivityMetrics {
  revenue = 'revenue', // (for agency_activity_top)
  account_ecpm = 'account_ecpm', // (for agency_activity_top)
  fill_rate = 'fill_rate', // (for agency_activity_top)
  server_calls = 'server_calls' // (for agency_activity_top)
}

export enum DashboardContentActivityMetrics {
  revenue = 'revenue', // (for content_activity_top)
  cpm = 'cpm', // (for content_activity_top)
  fill_rate = 'fill_rate', // (for content_activity_top)
  content_views = 'content_views' // (for content_activity_top)
}

export enum DashboardContentActivityInfoMetrics {
  revenue = 'revenue', // (for content_activity_info)
  cpm = 'cpm', // (for content_activity_info)
  fill_rate = 'fill_rate', // (for content_activity_info)
  content_views = 'content_views' // (for content_activity_info)
}

export interface DashboardPublisherActivity {
  server_calls: number
  server_calls_percentage_change: number
  revenue: number
  revenue_percentage_change: number
  viewable_impressions: number
  publisher_cost_server_calls_cpm: number
  publisher_cost_server_calls_cpm_percentage_change: number
  viewable_impressions_percentage_change: number
  viewable_fill: number
  viewable_fill_percentage_change: number
  revenue_timeline: any
  server_calls_timeline: any
  use_player_loads?: boolean
}

export interface DashboardPublisherQuality {
  server_calls: number
  server_calls_percentage_change: number
  revenue: number
  revenue_percentage_change: number
  viewable_impressions: number
  vcpm: number
  vcpm_percentage_change: number
  viewable_impression_percentage_change: number
  viewable_fill: number
  viewable_fill_percentage_change: number
  viewability_imps_timeline: any
  viewability_fill_timeline: any
}

export interface DashboardPublisherQualityInfo {
  server_calls_desktop_percentage: number
  server_calls_mobile_percentage: number
  cpm_desktop: number
  cpm_mobile: number
}

export interface DashboardPublisherActivityTop {
  countries: any
}

export interface DashboardPublisherQualityTop {
  videos: any
}

export interface DashboardAgencyAccount {
  organization_name: string
  ecpm: number
  viewable_impressions: number
  impressions: number
  commision: number
  master_account_cost: number
  revenue: number
  revenue_percentage_change: number
  fill_rate: number
  fill_rate_percentage_change: number
  server_calls: number
}

export interface DashboardAgencyAccounts {
  organizations: DashboardAgencyAccount[]
}

export interface DashboardAgencyActivity {
  server_calls: number
  server_calls_percentage_change: number
  fill_rate: number
  fill_rate_percentage_change: number
  vcpm: number
  vcpm_percentage_change: number
  revenue: number
  revenue_percentage_change: number
  agency_fee: number
  agency_fee_percentage_change: number
  revenue_timeline: any
  server_calls_timeline: any
  use_player_loads?: boolean

  //viewable_impressions: number
  //viewable_impression_percentage_change: number
  //viewable_fill: number
  //viewable_fill_percentage_change: number
}

export interface DashboardAgencyActivityTop {
  countries: any
}

export interface DashboardAgencyQuality {
  server_calls: number
  server_calls_percentage_change: number
  revenue: number
  revenue_percentage_change: number
  view_rate: number
  view_rate_percentage_change: number
  vcpm: number
  vcpm_percentage_change: number
  ad_viewability_timeline: any
  fill_rate_timeline: any
}

export interface DashboardAgencyQualityInfo {
  server_calls_desktop_percentage: number
  server_calls_mobile_percentage: number
  cpm_desktop: number
  cpm_mobile: number
}

export interface DashboardContentActivity {
  revenue: number
  revenue_percentage_change: number
  content_views: number
  content_views_percentage_change: number
  cpm: number
  cpm_percentage_change: number
  ads: number
  ads_percentage_change: number
  fill_rate: number
  fill_rate_percentage_change: number
  revenue_timeline: any
  content_views_timeline: any
}

export interface DashboardContentActivityInfo {
  content_views_desktop_percentage: number
  content_views_mobile_percentage: number
  domains: any
}

export interface DashboardContentActivityTop {
  countries: any
}

export enum LoadingState {
  idle = 'idle',
  initial = 'initial',
  loading = 'loading',
  more = 'more',
  success = 'success',
  error = 'error'
}

export enum Stacks {
  ModalOverlay = 'modal_overlay',
  SideContainers = 'side_containers'
}

export enum DataType {
  Folder = 'folders',
  Package = 'packages',
  Video = 'videos',
  Playlist = 'playlists',
  Widget = 'widgets',
  Account = 'accounts',
  PackageFolderComponent = 'packageFolders',
  ExcludedDomains = 'excludedDomains',
  OfferedPackages = 'proposals',
  MyDomains = 'domains',
  Users = 'users',
  PairedUrls = 'contentTargeting',
  ContentSources = 'streams',
  AgencyAccounts = 'organizations',
  Channel = 'personalChannel',
  VideoDraft = 'videoDraft',
  NewUpload = 'newUpload',
  ActiveApiKeys = 'activeApiKeys',
  RevokedApiKeys = 'revokedApiKeys',
  AiGenerated = 'aiGenerated',
  PendingAiGenerated = 'pendingAiGenerated'
}

export const DataTypeApiMapping = {
  folders: 'folder',
  packages: 'contentPackage',
  videos: 'videos',
  playlists: 'playlist',
  widgets: 'widget',
  accounts: 'organizationUser',
  packageFolders: 'folder',
  excludedDomains: 'content',
  proposals: 'contentPackage',
  domains: 'domain',
  users: 'organizationUser',
  contentTargeting: 'content',
  streams: 'videos',
  organizations: 'organization',
  personalChannel: 'channel',
  videoDraft: 'videoDraft',
  newUpload: 'newUpload',
  activeApiKeys: 'apiKeys',
  revokedApiKeys: 'apiKeys',
  aiGenerated: 'aiGenerated',
  pendingAiGenerated: 'aiGenerated'
}

export interface ListColumnTitle {
  title: string
  orderBy?: string
  sortable?: boolean
  sorted?: boolean
  sortBy?: string
}

export enum ContentTabs {
  Videos = 'videos',
  Folders = 'folders',
  Packages = 'packages'
}

export enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc'
}

export enum SortByOptions {
  UploadDate = 'upload_date',
  Title = 'title',
  DurationInMs = 'duration_in_ms',
  Likes = 'likes'
}

export type SortBy = SortByOptions | ''

export type FilterField = 'duration' | 'category' | 'pricing_model' | 'language'

export type SortOption = {
  label?: string
  sortBy: SortBy
  sortOrder: SortOrder
}

export type FilterOption = {
  label: string
  filterName: FilterField
}

export enum ListColumnAlignment {
  Left = 'left',
  Center = 'center',
  Right = 'right',
  default = 'default'
}

export type ListColumn = {
  [x: string]: any
  name: string
  title: string
  width: string | number
  alignment: ListColumnAlignment
  sortable?: boolean
  offset?: number
  tooltip?: string
  hideByCondition?: () => boolean
  minWidthInPx?: string
  maxWidthInPx?: string
  expand?: true
}

export interface ActionButton {
  icon?: string
  label: string
  onClick: () => void | Promise<void>
}

export enum DataViewType {
  Grid = 'grid',
  List = 'list'
}

export enum ReportType {
  console_publisher_truvid_report = 'console_publisher_truvid_report',
  console_publisher_content_report = 'console_publisher_content_report',
  console_publisher_native_clicks_report = 'console_publisher_native_clicks_report',
  console_content_content_report = 'console_content_content_report',
  console_accounts_account_report = 'console_accounts_account_report',
  console_engagement_report = 'console_user_engagement_report'
}

export enum DataViewCardSize {
  XXL = 400,
  XL = 350,
  L = 300,
  M = 250,
  S = 200,
  XS = 150,
  XXS = 100
}

export interface VideoComponentOptions {
  selected?: boolean
  bare?: boolean
  removeEmbedButton?: boolean
  removeAddButton?: boolean
  withBottom?: boolean
  imageIdx?: number
  threeDots?: boolean
  withTitleEvents?: boolean
  hoverAnimation?: boolean
  selectOnly?: boolean
  interactive?: boolean
  onClick?: () => void
  detailsLink?: string
  defaultCursor?: boolean
  playVideo?: boolean
  showTime?: boolean
  showOwnership?: boolean
  showTopText?: boolean
}

export enum CRUD {
  Add = 'add',
  Edit = 'edit',
  Delete = 'delete',
  MultipleDelete = 'multipleDelete',
  View = 'view',
  Duplicate = 'duplicate',
  Deactivate = 'deactivate',
  Activate = 'activate',
  Confirm = 'confirm',
  ConfirmClose = 'confirmClose',
  Continue = 'continue',
  CloseCreate = 'closeCreate',
  Unpair = 'unpair',
  ConfirmAccept = 'accept',
  ConfirmDecline = 'decline',
  CountryRestriction = 'countryRestriction'
}

export enum ContentPath {
  Domain = 0,
  Route = 1,
  DataType = 2,
  CRUD = 3,
  Param = 4
}

export enum Orientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal'
}

export interface DataActions {
  refreshData: () => void
  searchData?: (e: Ref<string>) => void
  clearSearch?: () => void
  resetToDefaultPageAndGetData?: () => void
}

export interface ContentDataRouterConfig {
  setIgnoreDataRouter: (val: boolean) => void
}

export enum CustomInputType {
  Number = 'number',
  String = 'text',
  Multiselect = 'multiselect',
  RadioGroup = 'radio',
  CheckBoxGroup = 'checkbox',
  Password = 'password',
  Textarea = 'textarea',
  Date = 'date'
  // ...
}

export type CustomInputValue = string | number | boolean | object | null

export enum InputIconActionType {
  // log the current input value
  Log = 'log',
  // clear the current input value
  Clear = 'clear'
  // ...
}

export enum SocialType {
  Likes = 'likes',
  Shares = 'shares',
  Views = 'views'
}

export enum SocialVariant {
  cardTop = 'top',
  cardBottom = 'bottom',
  list = 'list'
}

export interface ValidationParams {
  required?: boolean
  minLen?: number
  maxLen?: number
  minVal?: number
  step?: number
  maxVal?: number
  alphaNum?: boolean
  numeric?: boolean
  integer?: boolean
  email?: boolean
  url?: boolean
  InputUrlOrDomainRequired?: boolean
  domain?: boolean
  patternValidation?: RegExp
  checkboxN?: number
  multiN?: number
  isInUse?: boolean
  sameAs?: any
  notSameAs?: any
  customBackendValidationError?: boolean
  isValidStringDateTodayOrOlder?: boolean
  isValidStringDateLaterThanMinDate?: boolean
  isValidStringDate?: boolean
  validationFunc?: (...args: any[]) => any
  asyncValidation?: (...args: any[]) => any
  // ...
}

export interface ValidationObject {
  value: number | string | boolean | object | null
  valid: boolean
  id: string
  field?: string
}

export interface MobileFiltersColumn {
  id: string
  label: string
  selected: boolean
  disabled: boolean
}

export interface MobileFiltersDefaultStates {
  report: string
  groupBy: string
  timezone: string
  dateRange: string[]
  columns: MobileFiltersColumn[]
}

export interface CustomErrorMessages {
  required?: string
  minLen?: string
  maxLen?: string
  minVal?: string
  maxVal?: string
  alphaNum?: string
  numeric?: string
  integer?: string
  email?: string
  isInUse?: string
  url?: string
  domain?: string
  patternValidation?: string
  checkboxN?: string
  multiN?: string
  sameAs?: string
  notSameAs?: string
  asyncValidation?: string
  date?: string
  customBackendValidationError?: string
  // ...
}
export interface RadioGroupInput {
  id?: string
  label: string
  value: string | number | boolean | symbol | object
  tooltip?: string
  description?: string
  disabled?: boolean
}

export enum WidgetTypeFilter {
  InArticleHorizontal = 'in_article_horizontal',
  InArticleVertical = 'in_article_vertical',
  FloaterHorizontal = 'floater_horizontal',
  FloaterVertical = 'floater_vertical'
}

export interface CheckboxGroupInput {
  id: string
  label: string
  selected: boolean | null
}

export enum InputLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
  Mobile = 'mobile'
}

export enum SwitchColors {
  Blue = 'blue',
  Green = 'green'
}

export enum SwitchState {
  On = 'on',
  Off = 'off',
  Mixed = 'mixed'
}

export enum Devices {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Both = 'both'
}

export enum DeviceType {
  Desktop = 'desktop',
  Mobile = 'mobile'
}

export enum CountryRestrictionType {
  Exclude = 'exclude',
  Include = 'Include'
}

export interface NoDataBtn {
  txt: string
  route: string
}

export enum BaseRoutes {
  dashboard = 'dashboard',
  library = 'library',
  playlists = 'playlists',
  widgets = 'widgets',
  reports = 'reports',
  notifications = 'notifications',
  settings = 'settings',
  content = 'content',
  accounts = 'accounts',
  inventory = 'inventory',
  'domains-inventory' = 'domains-inventory',
  'offered-packages' = 'offered-packages',
  packages = 'packages',
  videos = 'videos',
  'my-feed' = 'my-feed',
  player = 'player',
  imports = 'imports',
  channel = 'channel',
  'ai-generated' = 'ai-generated',
}

export enum DeviceFilter {
  All = 'all',
  Desktop = 'desktop',
  Mobile = 'mobile'
}

export interface SideMenuItem {
  route: string
  permissions?: string[]
  device: Devices
  subItems?: SideMenuItem[]
  buttonOnly?: boolean
  showSubItems?: boolean
}

export interface ErrorMessage {
  error_message: string
}

export interface LeadRequest {
  id: string
  first_name: string
  last_name: string
  country_id: number
  language_id: number
  organization_name: string
  organization_website: string
  is_completed: boolean
}

export enum DashboardType {
  None = 0,
  PublisherDashboard = 1,
  ContentDashboard = 2,
  AgencyDashboard = 3
}

export const DataViewBreakpoints = {
  ExcludedDomains: { xs: 1, lg: 2, xl: 5, xxl: 6, xxxl: 10, hd: 15 },
  OfferedPackages: { xs: 6, lg: 6, xl: 6, xxl: 6, xxxl: 6, hd: 6 },
  Video: { xs: 2, lg: 2, xl: 3, xxl: 5, xxxl: 6, hd: 15 },
  Drafts: { xs: 7, lg: 7, xl: 7, xxl: 7, xxxl: 7, hd: 7 },
  Folder: { xs: 2, lg: 3, xl: 3, xxl: 6, xxxl: 10, hd: 15 },
  Package: { xs: 2, lg: 3, xl: 5, xxl: 7, xxxl: 10, hd: 15 },
  Playlist: { xs: 2, lg: 3, xl: 4, xxl: 5, xxxl: 10, hd: 15 },
  Widget: { xs: 2, lg: 3, xl: 5, xxl: 6, xxxl: 10, hd: 15 },
  MyDomains: { xs: 2, lg: 2, xl: 3, xxl: 4, xxxl: 10, hd: 15 },
  Users: { xs: 1, lg: 2, xl: 3, xxl: 4, xxxl: 5, hd: 15 },
  ContentSources: { xs: 2, lg: 3, xl: 5, xxl: 6, xxxl: 8, hd: 15 },
  AgencyAccounts: { xs: 1, lg: 3, xl: 4, xxl: 5, xxxl: 10, hd: 15 },
  PairedUrls: { xs: 2, lg: 3, xl: 5, xxl: 6, xxxl: 10, hd: 15 },
  Proposals: { xs: 2, lg: 2, xl: 3, xxl: 6, xxxl: 10, hd: 15 },
  ActiveApiKeys: { xs: 4, lg: 4, xl: 4, xxl: 4, xxxl: 4, hd: 4 },
  RevokedApiKeys: { xs: 4, lg: 4, xl: 4, xxl: 4, xxxl: 4, hd: 4 },
  AiGenerated: { xs: 2, lg: 3, xl: 5, xxl: 8, xxxl: 8, hd: 8 },
  PendingAiGenerated: { xs: 2, lg: 3, xl: 5, xxl: 7, xxxl: 7, hd: 7 }
}

export interface DataViewBreakpointValue {
  xs: number
  lg: number
  xl: number
  xxl: number
  xxxl: number
  hd: number
}

export enum ViewBreakPoints {
  xs = 'xs',
  xm = 'xm',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  xxl = 'xxl',
  xxxl = 'xxxl',
  hd = 'hd'
}

export type TextWrap = 'wrap' | 'nowrap' | 'balance' | 'pretty' | 'stable'

export enum DateRangeNames {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7days = 'last7days',
  Last30days = 'last30days',
  ThisMonth = 'thismonth',
  LastMonth = 'lastmonth',
  Custom = 'custom'
}

export interface TodoList {
  accountCreated: boolean
  domainsVisited: boolean
  copyADStxt: boolean
  checkADStxt: boolean
  billingInfoAvailable: boolean
  reportsVisited: boolean
  accountLogin: boolean
  domainCreated: boolean
  playlistCreated: boolean
  widgetCreated: boolean
  embedCodeCopied: boolean
  videoUploaded: boolean
  sourceImported: boolean
  profileUpdated: boolean
  packageCreated: boolean
  packageDistributed: boolean
}

export interface HeaderProps {
  icon?: string
  text?: string
  textSecondary?: string
  imageSpacing?: string
  isCloseIconHidden?: boolean
  progressBarProps?: { progressBarStep: number; totalProgressSteps: number }
  infoBarProps?: { message: string; isVisible: boolean; type: InfoBarTypes }
  textStyle?: Record<string, string>
  textSecondaryStyle?: Record<string, string>
}

export interface FooterProps {
  buttonCancel?: string
  buttonConfirm?: string
  isConfirmValid?: boolean
  confirmTooltip?: string
  rightAlignSingleButton?: boolean
  leftAlignSingleButton?: boolean
  cancelBtnVariant?: ButtonVariant
  confirmBtnVariant?: ButtonVariant
}

export interface CustomModalProps {
  text?: string
  textSecondary?: string
  buttonConfirm?: string
  buttonCancel?: string
  buttonConfirmText?: string
  buttonCancelText?: string
  noHeader?: boolean
  noFooter?: boolean
  noCustomComponent?: boolean
  height?: string
  width?: string
  margin?: string
  padding?: string
  maxWidth?: string
  maxHeight?: string
  componentPadding?: string
  preventCloseOnBackdropClick?: boolean
  action?: CRUD
  type?: DataType
  headerProps?: HeaderProps
  footerProps?: FooterProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDismiss?: ((arg: any) => Promise<void>) | ((arg?: any) => void)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConfirm?: ((arg: any) => Promise<void>) | ((arg?: any) => void)
  onCloseModalOnIcon?: ((arg: any) => Promise<void>) | ((arg?: any) => void)
  setFocusOnUnmount?: () => void
  isSubmitValid?: boolean
  extraProps?: object
  showDiscardStep?: boolean
  disableClickOutside?: boolean
  heroImage?: string
  isRebrandingStyle?: boolean // temporary prop to use new typography classes and new buttons design (<Button2/>)
  noBlur?: boolean
}

export interface ModalOpenParams {
  content: object
  header?: object
  footer?: object
  props?: object
}

export interface CustomConfirmationResponse {
  id?: string | number
  action: CRUD
  type: DataType
  field: string
  inputValue: string | number | object
  value: boolean
  forceQuit?: boolean
}

export interface CustomConfirmationActionEvent {
  actionType: string
  payload: CustomConfirmationResponse
}

export interface CustomConfirmationActionHandlers {
  confirmClose?: (params?: any) => any
  delete?: (params?: any) => any
  duplicate?: (params?: any) => any
  confirm?: (params?: any) => any
  deactivate?: (params?: any) => any
  activate?: (params?: any) => any
  add?: (params?: any) => any
  edit?: (params?: any) => any
  unpair?: (params?: any) => any
  view?: (params?: any) => any
  closeCreate?: (params?: any) => any
}

export enum boolStatusFilter {
  All = 'all',
  Active = 'true',
  Inactive = 'false'
}

export interface WidgetFilterValueMap {
  [FilterType.Status]: StatusFilter
  [FilterType.Device]: DeviceFilter
  [FilterType.Type]: WidgetTypeFilter[]
}

export interface PasswordValidationRule {
  key: string
  translationKey: string
}

export interface WidgetFilterState<T> {
  value: T
  tempValue: T
  isOpen: boolean
  isApplied: boolean
}

export type WidgetFiltersState = {
  [K in keyof WidgetFilterValueMap]: WidgetFilterState<WidgetFilterValueMap[K]>
}

export type DeviceFilterConfig = FilterConfig<FilterType.Device, DeviceFilter>
export type WidTypeFilterConfig = FilterConfig<FilterType.Type, WidgetTypeFilter[]>

export type WidgetFilterConfigArray = [
  FilterConfig<FilterType.Status, StatusFilter>,
  FilterConfig<FilterType.Device, DeviceFilter>,
  FilterConfig<FilterType.Type, WidgetTypeFilter[]>
]

export type HorizontalVideo = UploadVideo & { is_vertical: false }
export type VerticalVideo = UploadVideo & { is_vertical: true }

type videoLocalId = string
export type ModifiedVideoDataMap = Record<videoLocalId, Partial<UploadVideo>>

export enum NewVideoCardListType {
  Horizontal = 'horizontal',
  Vertical = 'vertical'
}

export interface DropdownScopeProps {
  close: () => void
  open: () => void
}
export enum StencilTypes {
  Rectangle = 'rectangle',
  Circle = 'circle'
}

export enum CropperAspectRatios {
  Profile = 1,
  Cover = 3.65,
  VerticalThumbnail = 9 / 16,
  HorizontalThumbnail = 16 / 9,
  PackageCover = 389 / 110
}

export interface ImageSizeRestrictionDatum {
  width: number
  height: number
}

export type ImageSizeRestriction = Record<ImageTypes, ImageSizeRestrictionDatum>
export type VueComponentContent = Component
export type VueComponentHeader = Component
export type VueComponentFooter = Component

export interface IModalBase {
  modalType: ModalType | null
  modalState: {
    componentContent: VueComponentContent | null
    componentHeader: VueComponentHeader | null
    componentFooter: VueComponentFooter | null
    props: CustomModalProps
    payload: Record<string, unknown>
  }
  loading: boolean
  isSubmitting: boolean
  isCanceling: boolean
}

export interface ModalTypePayload {
  modalType: ModalType | null
}

export interface PostVideoError {
  video_title: string
  error: string
}

export interface PostVideoResponse {
  new_video_ids: string[]
  errors: PostVideoError[]
}

export enum NewVideoStatuses {
  NoSettings = 'no_settings',
  SettingsSet = 'settings_set',
  UploadFailed = 'upload_failed'
}

export enum NewVideoStatusFilterOptions {
  All = 'all',
  NoSettings = 'no_settings',
  SettingsSet = 'settings_set',
  UploadFailed = 'upload_failed'
}

export enum SummaryPartType {
  Settings = 'settings',
  NoSettings = 'noSettings',
  Failed = 'failed'
}

export interface SummaryPart {
  text: string
  type: SummaryPartType
}

export type SettingsPayloadValue =
  | number
  | boolean
  | string
  | number[]
  | string[]
  | null
  | { url: string }[]
  | VideoTag[]

export interface VideoSettingsPayload {
  language_id?: number | null
  private?: boolean | null
  pricing_model?: string
  folder?: number | null
  categories?: number[] | null
  hasCountryRestrictions?: boolean | null
  countryRestrictionType?: string | null
  countries_allow?: string[] | null
  countries_disallow?: string[] | null
  guid?: string
  clickthroughUrls?: string
  tags?: string | null
  isSettingsUpdated?: boolean
}

export interface VideoMetadata {
  isVertical: boolean
  durationInMs: number
}

export interface InfoBarSnapshot {
  type: InfoBarTypes
  message: string
}

export interface VideoPollingCallbacks {
  onRefreshData?: () => void
  onProcessingComplete?: () => void
}

export interface VideoPollingOptions {
  interval?: number
  userId?: number
  callbacks?: VideoPollingCallbacks
}

export interface SuccessfulVideoInfo {
  id: string
  title: string
  thumbnail_url: string | null
}

export interface CountryRestrictionValue {
  hasRestrictions: boolean | null
  restrictionType: CountryRestrictionType | null
  countriesAllow: string[] | null
  countriesDisallow: string[] | null
}

export interface CountryRestrictionConflicts {
  hasRestrictions: boolean
  restrictionType: boolean
  countriesAllow: boolean
  countriesDisallow: boolean
}

export interface CountryRestrictionPlaceholders {
  countriesAllow?: string
  countriesDisallow?: string
}

export interface CountryRestrictionValidation {
  didTryToSubmit: boolean
  validationKey: string
}

export interface ProposalMessages {
  success?: string
  error?: string
}

export type ConfirmationChangeParams = { action: CRUD; type: string; field: string; value?: boolean }
